import {
  CUSTOM_QUESTION_TYPES,
  type CustomQuestion,
  type CustomQuestionType,
  type Entity,
} from "../types";

/**
 * Keys in `customResponses` the app already gives a meaning to. A question
 * can't be stored under one of these, or its answer would be read as a
 * scheduled time, a location, the orders list, and so on.
 */
export const RESERVED_RESPONSE_KEYS = new Set([
  "activityType",
  "entityType",
  "scheduled_time",
  "scheduled_boarding",
  "from_airport",
  "to_airport",
  "location",
  "host",
  "orders",
  "rounds",
  "hasTrivia",
  "questions",
  "score",
  "winningScore",
]);

function isQuestionType(t: unknown): t is CustomQuestionType {
  return typeof t === "string" && (CUSTOM_QUESTION_TYPES as readonly string[]).includes(t);
}

/**
 * The questions an entity asks, in order. Reads the old app's shape as it was
 * migrated — `options` may be missing on a select that only carried
 * `optionsText`, `order` may be absent, `required` may be absent — and drops
 * anything without a usable id and label rather than rendering a broken row.
 */
export function getCustomQuestions(entity: Pick<Entity, "customConfig"> | null | undefined): CustomQuestion[] {
  const raw = entity?.customConfig?.custom_questions;
  if (!Array.isArray(raw)) return [];
  const out: CustomQuestion[] = [];
  raw.forEach((item, index) => {
    if (!item || typeof item !== "object") return;
    const q = item as Record<string, unknown>;
    const id = typeof q.id === "string" ? q.id.trim() : "";
    const label = typeof q.label === "string" ? q.label.trim() : "";
    if (!id || !label) return;
    const type: CustomQuestionType = isQuestionType(q.type) ? q.type : "text";
    const order = typeof q.order === "number" && Number.isFinite(q.order) ? q.order : index + 1;
    const question: CustomQuestion = { id, label, type, required: q.required === true, order };
    if (type === "select") {
      let options: string[] = [];
      if (Array.isArray(q.options)) {
        options = q.options.filter((o): o is string => typeof o === "string");
      } else if (typeof q.optionsText === "string") {
        options = q.optionsText.split("\n");
      }
      question.options = options.map((o) => o.trim()).filter(Boolean);
    }
    out.push(question);
  });
  return out.sort((a, b) => a.order - b.order);
}

/**
 * A storage key for a new question, from its label: "Row number" → "row_number".
 * Stays clear of the app's own keys and of the entity's other questions, so an
 * answer never lands on top of something else.
 */
export function questionIdFromLabel(label: string, taken: Iterable<string>): string {
  const base =
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40) || "question";
  const used = new Set(taken);
  let id = base;
  let n = 2;
  while (RESERVED_RESPONSE_KEYS.has(id) || used.has(id)) {
    id = `${base}_${n++}`;
  }
  return id;
}

/**
 * What the entity form writes: labels trimmed, blank rows and blank options
 * dropped, `order` renumbered to match the list, and every question given an
 * id if the editor hasn't yet.
 */
export function normalizeCustomQuestions(questions: CustomQuestion[]): CustomQuestion[] {
  const taken = new Set<string>();
  const out: CustomQuestion[] = [];
  for (const q of questions) {
    const label = q.label.trim();
    if (!label) continue;
    // Answers are saved by dotted field path (`customResponses.<id>`), so an
    // id has to be a plain word; anything else is re-keyed from the label.
    const existing = q.id.trim();
    const id =
      existing && /^\w+$/.test(existing) && !RESERVED_RESPONSE_KEYS.has(existing) && !taken.has(existing)
        ? existing
        : questionIdFromLabel(label, taken);
    taken.add(id);
    const normalized: CustomQuestion = {
      id,
      label,
      type: q.type,
      required: q.required === true,
      order: out.length + 1,
    };
    if (q.type === "select") {
      normalized.options = (q.options ?? []).map((o) => o.trim()).filter(Boolean);
    }
    out.push(normalized);
  }
  return out;
}

/** The stored answer to a question as a string for an input, or "" when unset. */
export function customAnswer(
  responses: Record<string, unknown> | null | undefined,
  question: CustomQuestion,
): string {
  const v = responses?.[question.id];
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}

/**
 * The value to store for an answer typed into an input: numbers as numbers,
 * anything blank as `null` so a cleared field reads as unanswered.
 */
export function customAnswerValue(question: CustomQuestion, input: string): string | number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (question.type === "number") {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return trimmed;
}

/** Questions the entity requires that this activity has no answer to yet. */
export function unansweredRequired(
  questions: CustomQuestion[],
  responses: Record<string, unknown> | null | undefined,
): CustomQuestion[] {
  return questions.filter((q) => q.required && customAnswer(responses, q) === "");
}

/**
 * The answered questions, as label/value pairs ready to show on a card. An
 * answer to a question the entity no longer asks is kept out of sight rather
 * than shown under a key nobody would recognise.
 */
export function answeredQuestions(
  questions: CustomQuestion[],
  responses: Record<string, unknown> | null | undefined,
): { question: CustomQuestion; value: string }[] {
  return questions
    .map((question) => ({ question, value: customAnswer(responses, question) }))
    .filter((a) => a.value !== "");
}

/**
 * The answers to carry through a write that rebuilds `customResponses` from
 * the edit form: whatever the entity's questions currently hold. Without this
 * an edit to the activity's name would drop the song picked on the ride.
 */
export function pickCustomAnswers(
  questions: CustomQuestion[],
  responses: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const q of questions) {
    const v = responses?.[q.id];
    if (v != null && v !== "") out[q.id] = v;
  }
  return out;
}
